import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { AuditService } from '@app/services/audit.service';
import { RestapiService } from '@app/services/restapi.service';
import { AuditableEntityType } from '@shared/enums/auditable-entity-type';
import { RestDelinquencyModel } from '@shared/interfaces/delinquency.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  getDelinquencyChangesHistory,
  getDelinquencyChangesHistoryFailure,
  getDelinquencyChangesHistorySuccess,
  loadDelinquencyById,
  loadDelinquencyByIdFailed,
  loadDelinquencyByIdSucceded,
  loadDelinquencyList,
  loadDelinquencyListFailed,
  loadDelinquencyListSucceded,
  updateDelinquencyDetailsById,
} from './delinquency.actions';

@Injectable()
export class DelinquencyEffects {
  loadDelinquencyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDelinquencyList),
      switchMap(action =>
        this.restApiService.getData<any[]>(`Delinquencies/property/${action.id}`).pipe(
          map(delinquencyList => loadDelinquencyListSucceded({ delinquencyList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DL01RetrievingDelinquencyList);
            return of(loadDelinquencyListFailed());
          })
        )
      )
    )
  );

  loadDelinquencyById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDelinquencyById),
      switchMap(action =>
        this.restApiService.getData<any[]>(`Delinquencies/${action.delinquencyId}`).pipe(
          map(delinquencyDetails => loadDelinquencyByIdSucceded({ delinquencyDetails })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DL02RetrievingDelinquencyList);
            return of(loadDelinquencyByIdFailed());
          })
        )
      )
    )
  );

  updateDelinquencyById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDelinquencyDetailsById),
      mergeMap(action =>
        this.restApiService
          .update<RestDelinquencyModel>(`Delinquencies/${action.delinquencyDetails.id}`, action.delinquencyDetails)
          .pipe(
            mergeMap(delinquencyDetails => [
              loadDelinquencyByIdSucceded({ delinquencyDetails }),
              getDelinquencyChangesHistory({ objectId: delinquencyDetails.id }),
            ]),
            catchError(() => {
              this.snackbarService.error(SnackbarErrorMessage.UpdatingDelinquency);
              return of(loadDelinquencyByIdFailed());
            })
          )
      )
    )
  );

  getChangesHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDelinquencyChangesHistory),
      switchMap(action =>
        this.auditService.getChangesHistory(action.objectId, AuditableEntityType.Delinquency).pipe(
          map(auditModelList => getDelinquencyChangesHistorySuccess({ auditModelList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingDelinquencyHistory);
            return of(getDelinquencyChangesHistoryFailure());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private restApiService: RestapiService,
    private snackbarService: SnackbarService,
    private auditService: AuditService
  ) {}
}
